<template>
  <div class="container is-fluid py-5">
    <button
      :disabled="!valid" 
      class="button is-primary mb-4"
      @click="saveAssemblySheet()">
      Save Assembly Sheet
    </button>

    <v-form :valid.sync="valid" class="columns">
      <div class="column is-one-fifth">
        <label class="label">Project*</label>
        <v-select 
          v-if="projects.length > 0"
          required
          :options="projects"
          item-text="project_name"
          item-value="id"
          v-model="projectId"
        />
        <span class="help">Only projects with cut sheets are listed</span>
      </div>
      <div class="column is-one-fifth">
        <label class="label">Assembly Sheet Condition</label>
        <v-text-field 
          placeholder="Enter assembly sheet condition"
          v-model="assemblySheet.condition">
        </v-text-field>
      </div>
      <div class="column is-one-fifth">
        <label class="label">Description</label>
        <v-text-field 
          placeholder="Enter assembly sheet description"
          v-model="assemblySheet.desc">
        </v-text-field>
      </div>
      <div class="column is-one-fifth">
        <label class="label">Assembly Sheet Upload*</label>
        <v-file-upload
          required
          file-type-validator=".*"
          v-model="assemblySheetUpload">
        </v-file-upload> 
      </div>
      <div class="column is-one-fifth">
        <label class="label">Total Output Parts</label>
        <v-text-field 
          required
          v-model="assemblySheet.finished_qty"
          placeholder="Assembly sheet output parts"/>
      </div>
    </v-form>

    <hr/>
    
    <div class="columns">
      <div class="column is-5">
        <div class="menu-label">Project Parts</div>

        <article 
          v-if="cutSheetParts.length == 0" 
          class="message is-info is-fullwidth">
          <div class="message-body">
            <span v-if="!projectId">
              Choose a project to see a list of parts that require assembly
            </span>
            <span v-else>
              There are no parts for this project that require assembly
              <div class="help">
                Note: if there are parts for this project that require assembly, 
                they be be in an existing assembly sheet.
              </div>
            </span>
          </div>
        </article>

        <table 
          class="table is-fullwidth has-small-font" 
          v-if="cutSheetParts.length > 0">
          <thead>
            <tr>
              <th></th>
              <th 
                v-for="(header, headerIndex) in headers"
                :key="headerIndex">
                {{ header.label }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr               
              v-for="part in cutSheetParts"
              :key="part.id">
              <td>
                <v-checkbox
                  :value="assemblyPartIds.indexOf(part.cutsheetpart_id) != -1"
                  @input="togglePart(part)">
                </v-checkbox>
              </td>
              <td>
                <router-link 
                  :to="{ name: 'CutSheetViewer', cutSheetId: part.cutsheet.id }">
                  {{ part.cutsheet.cutsheet_no }}
                </router-link>
              </td>
              <td class="is-size-7">
                {{ part.cutsheet.input_product_master.name }}
              </td>
              <td>
                {{ part.part_no }}
              </td>
              <td>
                {{ part.length_in_str }}
              </td>
              <td>
                {{ part.width_in_str }}
              </td>
              <td>
                {{ part.quantity | round(0) }}
              </td>
              <td>
                {{ part.qty_to_assemble ? +part.quantity_with_assembly + +part.qty_to_assemble :
                  part.quantity_with_assembly | round(0) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="column is-7">

        <div class="menu-label">Assembly Sheet Parts</div>
        <table 
          class="table is-fullwidth has-small-font" 
          v-if="assemblySheet.details.length > 0">
          <thead>
            <tr>
              <th 
                v-for="(header, headerIndex) in headers"
                :key="headerIndex">
                {{ header.label }}
              </th>
              <th>Qty to Assemble</th>
              <th>Assembly Details</th>
            </tr>
          </thead>
          <tbody>
            <tr               
              v-for="part in assemblySheet.details"
              :key="part.id">
              <td>
                <router-link 
                  :to="{ name: 'CutSheetViewer', cutSheetId: part.cutsheet.id }">
                  {{ part.cutsheet.cutsheet_no }}
                </router-link>
              </td>
              <td class="is-size-7">
                {{ part.cutsheet.input_product_master.name }}
              </td>
              <td>
                {{ part.part_no }}
              </td>
              <td>
                {{ part.length_in_str }}
              </td>
              <td>
                {{ part.width_in_str }}
              </td>
              <td>
                {{ part.quantity | round(0) }}
              </td>
              <td>
                {{ part.qty_to_assemble ? +part.quantity_with_assembly + +part.qty_to_assemble :
                  part.quantity_with_assembly | round(0) }}
              </td>
              <td>
                <v-text-field
                  placeholder="# of Parts to Assemble"
                  v-model="part.qty_to_assemble"
                  type="number"
                  :rules="[v => v <= +part.quantity ? true : 
                    'Must be <= total quantity']">
                </v-text-field>
              </td>
              <td>
                <v-text-field
                  placeholder="Enter part assembly details"
                  v-model="part.section">
                </v-text-field>
              </td>
            </tr>
          </tbody>
        </table>

      </div>
    </div>
  </div>
</template>

<script>
import VCheckbox from '@/components/base/VCheckbox'
import VForm from '@/components/base/VForm'
import VTextField from '@/components/base/VTextField'
import VSelect from '@/components/base/VSelect'
import VFileUpload from '@/components/base/VFileUpload'
import { mapActions, mapMutations } from 'vuex'

export default {
  components: { VForm, VTextField, VSelect, VFileUpload, VCheckbox },
  data() {
    return {
      projectId: null,
      assemblySheet: {
        condition: null,
        description: null,
        finished_qty: null,
        details: []
      },
      assemblySheetUpload: {
        file: null,
        name: null,
        url: null
      },
      valid: false,
      cutSheetParts: [],
      headers: [
        { label: 'Cutsheet #' },
        { label: 'Input Slab Product' },
        { label: 'Part #' },
        { label: 'L"' },
        { label: 'W"'},
        { label: 'Qty' },
        { label: 'Qty in Assembly Sheet' }
      ],
      projects: []
    }
  },
  computed: {
    assemblyPartIds() {
      return this.assemblySheet.details.map(d => d.id);
    }
  },
  watch: {
    projectId(id) {
      this.getCutSheetParts(id);
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("assembly", ["addAssemblySheet", "addAttachment"]),
    ...mapMutations("app", ["setNotification"]),
    initialize() {
      this.$http.projects.get(
        { has_cutsheets: true, per_page: 1000 }
      ).then(resp => {
        this.projects = resp.data.results;
      })
    },
    getCutSheetParts(projectId) {
      this.$http.cutsheetParts.get(
        { 
          project_id: projectId,
          without_assembly: true,
          per_page: 1000 
        }
      ).then(
        resp => this.getCutSheetPartsSuccess(resp.data.results)
      )
    },
    getCutSheetPartsSuccess(data) {
      this.cutSheetParts = data.filter(d => d.requires_assembly == true);
    },
    togglePart(part) {
      const index = this.assemblyPartIds.indexOf(part.id);
      if (index == -1) {
        this.assemblySheet.details.push(part);
      } else {
        this.assemblySheet.details.splice(index, 1);
      }
    },
    saveAssemblySheet() {
      let payload = Object.assign({}, this.assemblySheet);
      payload.details = this.assemblySheet.details.map(d => {
        return {
          cutsheetpart_id: d.id,
          part_no: d.part_no,
          qty_to_assemble: d.qty_to_assemble,
          section: d.section
        }
      })

      this.addAssemblySheet({ 
        data: payload, 
        parameters: { project_id: this.projectId } 
      }).then(resp => {
        if (this.assemblySheetUpload.file) {
          let formData = new FormData();
          formData.append(
            'attachment', 
            this.assemblySheetUpload.file, 
            this.assemblySheetUpload.file.name
          );

          this.addAttachment({ 
            assemblySheetId: resp.data.id,  
            attachment: formData
          }).then(
            () => this.$router.push({ name: 'AssemblyList' })
          );
        } else {
          this.$router.push({ name: 'AssemblyList' });
        }
      })
    }
  }
}
</script>

<style scoped>
.buttons-right {
  justify-content: flex-end;
  flex-wrap: nowrap;
}

.table td {
  vertical-align: middle;
}
</style>